/**
 * 서버 설치 매뉴얼 — 콘솔이 그리는 매뉴얼과, 관리자(sudo)에게 보낼 명령 만들기.
 *
 * **매뉴얼 본문은 패키지의 설치안내와 같은 파일이다**(`deploy/package/guide-*.md`). 빌더가 zip 안의
 * `INSTALL-GUIDE.md` 로 채워 넣는 원본을 콘솔도 그대로 채워 그린다. 따로 쓰면 둘 중 하나는 반드시 낡는다 —
 * 현장에서 USB 의 안내서와 콘솔의 매뉴얼이 다른 말을 하면 사람은 어느 쪽도 믿지 않는다.
 * 판 번호도 빌더와 같은 곳(`deploy/package/versions.json`)에서 읽는다.
 *
 * **관리자 명령은 설치기가 보여 주는 명령과 같아야 한다.** 설치기(`deploy/package/docker/setup.sh`)는 이 서버에
 * 필요한 것만 골라 보여 주지만, 그것은 현장에서 `--check` 를 돌린 뒤에야 알 수 있다. 방문 전에 관리자에게 미리
 * 보내 두려고 같은 명령을 여기서 만든다. AppArmor 프로필은 시험이 설치기의 원문과 대조한다.
 */
import versionsRaw from '../../../deploy/package/versions.json?raw';

export type ManualVariant = 'docker' | 'native';
export type ManualArch = 'x64' | 'arm64';
export type PackageManager = 'apt' | 'dnf';
/** Docker 판의 엔진 — 이 사용자 전용(rootless) 또는 서버에 이미 있는 Docker(docker 그룹). */
export type DockerMode = 'rootless' | 'system';

interface ArchVersions {
  minio: string;
  mongoTargets: string[];
  mongoCompat: boolean;
}

export interface PackageVersions {
  node: string;
  mongo: string;
  mongoCompat: string;
  caddy: string;
  docker: string;
  compose: string;
  nginx: string;
  arch: Record<ManualArch, ArchVersions>;
}

export const VERSIONS = JSON.parse(versionsRaw) as PackageVersions;

export const VARIANT_TITLE: Record<ManualVariant, string> = {
  docker: 'Docker 판',
  native: '네이티브 판',
};

/**
 * 설치안내 원본. 매뉴얼 탭을 열 때만 받는다 — 목록 화면의 번들을 무겁게 하지 않게.
 *
 * 뒤에 **+ Sentinel 에이전트 판 절**(`guide-grid.md`)을 붙인다. 빌더는 그 판(`-sentinel-agent`) 패키지에만 붙이지만, 콘솔의 매뉴얼은 두 판을
 * 고르는 사람이 읽는 것이라 늘 싣는다 — 이음매는 빌더(`package-server.mjs` 의 `guide`)와 같다.
 */
export function loadGuide(variant: ManualVariant): Promise<string> {
  const main =
    variant === 'docker'
      ? import('../../../deploy/package/guide-docker.md?raw').then((module) => module.default)
      : import('../../../deploy/package/guide-native.md?raw').then((module) => module.default);
  const grid = import('../../../deploy/package/guide-grid.md?raw').then((module) => module.default);
  return Promise.all([main, grid]).then(([body, gridPart]) => `${body.trimEnd()}\n\n${gridPart}`);
}

/**
 * 내려받는 PDF 의 파일 이름.
 *
 * **아스키로 둔다.** 현장에서 이 파일은 USB·윈도우 노트북·메일을 거치는데, 그 사이에 한글 이름이
 * 한 번씩 깨진다(패키지 zip 이름을 아스키로 둔 것과 같은 이유 — 노트북 .51 에서 겪었다).
 * 판을 모르면 날짜를 쓴다 — 같은 이름 두 개가 다운로드 폴더에서 덮어쓰지 않게.
 */
export function installGuideFilename(variant: ManualVariant, version: string | null): string {
  const stamp = version ?? new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `signageone-install-guide-${stamp}-${variant}.pdf`;
}

/**
 * 설치안내의 `{{…}}` 자리에 넣을 값 — 빌더의 `guide()` 와 같은 뜻이다.
 *
 * 콘솔은 어느 패키지를 받을지 아직 모를 수 있다. 판을 모르면 판 자리를 사람이 읽을 수 있는 말로 채운다.
 */
export function guideValues(input: {
  variant: ManualVariant;
  arch?: ManualArch;
  /** 올라와 있는 패키지의 판(예: `20260917-b88d113`). 없으면 일반 표현으로 채운다. */
  version?: string | null;
}): Record<string, string> {
  const arch = input.arch ?? 'x64';
  const archVersions = VERSIONS.arch[arch];
  const version = input.version ?? null;
  const commit = version && version.includes('-') ? version.slice(version.indexOf('-') + 1) : '—';
  return {
    VERSION: version ?? '콘솔에 올라온 최신 판',
    COMMIT: commit,
    ARCH: arch === 'x64' ? 'x86_64 (amd64)' : 'ARM64 (aarch64)',
    FOLDER: `signageone-server-${version ?? '<판>'}-${input.variant}-${arch}`,
    NODE: VERSIONS.node,
    MONGO: VERSIONS.mongo,
    MINIO: archVersions.minio,
    CADDY: VERSIONS.caddy,
    DOCKER: VERSIONS.docker,
    COMPOSE: VERSIONS.compose,
    NGINX: VERSIONS.nginx,
    MONGO_TARGETS: archVersions.mongoTargets.join(', '),
    MONGO_COMPAT: archVersions.mongoCompat ? VERSIONS.mongoCompat : '(이 CPU 용 패키지에는 없음)',
    // Sentinel 에이전트는 패키지를 구울 때 dist/sentinel 의 판이 실린다 — 콘솔은 그 판을 모른다.
    SENTINEL_VERSION: '+ Sentinel 에이전트 판 패키지에 든 판(폴더의 sentinel/manifest.json)',
  };
}

/** `{{KEY}}` 를 채운다. 모르는 자리는 그대로 남긴다 — 시험이 남은 자리를 잡는다. */
export function fillGuide(template: string, values: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (whole, key: string) => values[key] ?? whole);
}

// ───────────────────────────── 관리자 명령 만들기 ─────────────────────────────

export interface AdminInput {
  variant: ManualVariant;
  /** 설치할(서비스를 운영할) 일반 사용자 계정 */
  user: string;
  /** 설치 위치. 설치기의 기본값은 그 사용자의 `$HOME/signageone` 이다. */
  prefix: string;
  port: number;
  packageManager: PackageManager;
  /** Docker 판에서만 본다. */
  dockerMode: DockerMode;
}

export interface AdminStep {
  id: string;
  title: string;
  /** 언제 필요한가 — 사람이 이 서버에 해당하는지 판단할 한 줄. */
  when: string;
  /** 이 서버에 필요한지 확인하는 명령(방화벽 외에는 sudo 없이 된다). 없으면 판단 기준이 `when` 뿐이다. */
  check?: string;
  /** 관리자에게 보낼 명령. */
  commands: string;
  note?: string;
}

const USER_RE = /^[a-z_][a-z0-9_-]{0,31}$/;
// 경로는 명령에 따옴표 없이 들어간다. 셸이 다르게 읽을 글자(공백·따옴표·$ 등)는 처음부터 받지 않는다.
const PREFIX_RE = /^\/[A-Za-z0-9._/-]+$/;

export function defaultPrefix(user: string): string {
  return `/home/${user || '<사용자>'}/signageone`;
}

/** 입력의 잘못을 사람이 읽을 말로. 비어 있으면 명령을 만들 수 있다. */
export function validateAdminInput(input: AdminInput): string[] {
  const errors: string[] = [];
  if (!USER_RE.test(input.user)) {
    errors.push('사용자 이름은 영문 소문자로 시작하고 영문 소문자·숫자·-·_ 만 씁니다(32자까지).');
  }
  const prefix = input.prefix.replace(/\/+$/, '');
  if (!PREFIX_RE.test(prefix) || prefix.split('/').includes('..') || prefix === '') {
    errors.push('설치 위치는 / 로 시작하는 경로여야 하고 영문·숫자·. - _ / 만 씁니다(공백·따옴표 불가).');
  }
  if (!Number.isInteger(input.port) || input.port < 1 || input.port > 65535) {
    errors.push('포트는 1~65535 사이의 숫자입니다.');
  }
  return errors;
}

/**
 * AppArmor 프로필 이름 — 설치기의 `apparmor_profile_name` 과 같은 규칙이다.
 * `<설치 위치>/docker/bin/rootlesskit` 에서 맨 앞 `/` 를 떼고 나머지 `/` 를 `.` 로.
 */
export function apparmorProfileName(prefix: string): string {
  const engine = `${prefix.replace(/\/+$/, '')}/docker`;
  return `${engine.replace(/^\//, '')}/bin/rootlesskit`.replaceAll('/', '.');
}

/** AppArmor 프로필을 넣는 명령 — 설치기가 보여 주는 것과 글자 하나까지 같다(시험이 대조한다). */
export function apparmorCommands(prefix: string): string {
  const clean = prefix.replace(/\/+$/, '');
  const name = apparmorProfileName(clean);
  return [
    `sudo tee /etc/apparmor.d/${name} >/dev/null <<'EOT'`,
    'abi <abi/4.0>,',
    'include <tunables/global>',
    '',
    `"${clean}/docker/bin/rootlesskit" flags=(unconfined) {`,
    '  userns,',
    '',
    `  include if exists <local/${name}>`,
    '}',
    'EOT',
    `sudo apparmor_parser -r /etc/apparmor.d/${name}`,
  ].join('\n');
}

/**
 * 이 서버에 필요할 수 있는 관리자 작업 — 매뉴얼 부록 A 와 같은 순서다.
 *
 * "필요할 수 있는" 것까지 모두 낸다. 무엇이 실제로 필요한지는 서버마다 다르고, 그 판단은 `check` 와
 * 설치기의 `--check` 가 한다. 빠진 것 때문에 현장에서 멈추는 것보다 해당 없는 한 줄을 건너뛰는 편이 싸다.
 */
export function adminSteps(input: AdminInput): AdminStep[] {
  const { user, port, packageManager: pm, variant } = input;
  const prefix = input.prefix.replace(/\/+$/, '');
  const steps: AdminStep[] = [];

  steps.push({
    id: 'account',
    title: '설치할 계정 만들기',
    when: `'${user}' 계정이 아직 없을 때`,
    check: `id ${user}`,
    commands: pm === 'apt' ? `sudo adduser ${user}` : `sudo useradd -m ${user} && sudo passwd ${user}`,
    note: '설치와 운영은 이 계정으로 직접 로그인(콘솔 또는 SSH)해서 합니다.',
  });

  steps.push({
    id: 'usb',
    title: 'USB 연결',
    when: '글자 화면만 있는 서버라 USB 가 자동으로 연결되지 않을 때',
    check: 'lsblk -o NAME,SIZE,FSTYPE,LABEL,MOUNTPOINT',
    commands: [
      'lsblk -o NAME,SIZE,FSTYPE,LABEL,MOUNTPOINT    # USB 장치 이름 찾기 (예: sdb1)',
      'sudo mkdir -p /mnt/usb',
      'sudo mount /dev/sdb1 /mnt/usb                 # sdb1 을 찾은 이름으로',
      '',
      '# 설치가 끝나 USB 를 뺄 때',
      'sudo umount /mnt/usb',
    ].join('\n'),
    note: '한 자리(/mnt/usb)에는 USB 하나만 연결합니다. mount | grep /mnt/usb 가 두 줄 이상이면 umount 를 그만큼 반복합니다.',
  });

  if (variant === 'docker' && input.dockerMode === 'rootless') {
    steps.push({
      id: 'uidmap',
      title: '전용 Docker ① uidmap',
      when: 'newuidmap 이 없을 때',
      check: 'command -v newuidmap || echo 없음',
      commands: pm === 'apt' ? 'sudo apt-get install -y uidmap' : 'sudo dnf install -y shadow-utils',
    });
    steps.push({
      id: 'subuid',
      title: '전용 Docker ② 하위 UID/GID 범위',
      when: `/etc/subuid 에 '${user}' 가 없을 때(adduser 로 만든 계정은 보통 이미 있습니다)`,
      check: `grep "^${user}:" /etc/subuid /etc/subgid || echo 없음`,
      commands: `sudo usermod --add-subuids 100000-165535 --add-subgids 100000-165535 ${user}`,
      note: '범위는 다른 사용자와 겹치지 않게 고릅니다(cat /etc/subuid 로 확인).',
    });
    if (pm === 'apt') {
      steps.push({
        id: 'apparmor',
        title: '전용 Docker ③ AppArmor 허용',
        when: 'Ubuntu 23.10 이상에서 아래 확인 명령이 1 일 때',
        check: 'cat /proc/sys/kernel/apparmor_restrict_unprivileged_userns',
        commands: apparmorCommands(prefix),
        note: `이 설치(${prefix})의 rootlesskit 하나에만 허용합니다. 설치 위치를 바꾸면 이 명령도 다시 만들어야 합니다.`,
      });
    }
  }

  if (variant === 'docker' && input.dockerMode === 'system') {
    steps.push({
      id: 'docker-group',
      title: '서버 Docker 쓰기 — docker 그룹',
      when: '서버에 Docker 가 이미 있고, 전용 Docker 대신 그것을 쓸 때',
      check: 'docker info >/dev/null 2>&1 && echo 쓸 수 있음 || echo 쓸 수 없음',
      commands: `sudo usermod -aG docker ${user}\nsudo systemctl enable --now docker`,
      note: '적용하려면 그 사용자가 로그아웃했다가 다시 로그인해야 합니다. docker 그룹은 사실상 관리자 권한과 같습니다 — 보안 담당과 확인하세요.',
    });
  } else {
    steps.push({
      id: 'linger',
      title: '자동 시작(linger)',
      when: '설치기가 스스로 켜지 못했을 때 — 미리 켜 두면 확실합니다',
      check: `loginctl show-user ${user} -p Linger`,
      commands: `sudo loginctl enable-linger ${user}`,
    });
  }

  steps.push({
    id: 'firewall',
    title: '방화벽',
    when: '다른 PC·화면에서 접속이 안 될 때(방화벽이 켜진 서버)',
    check: pm === 'apt' ? 'sudo ufw status' : 'sudo firewall-cmd --state',
    commands:
      pm === 'apt'
        ? `sudo ufw allow ${port}/tcp`
        : `sudo firewall-cmd --permanent --add-port=${port}/tcp && sudo firewall-cmd --reload`,
    note: pm === 'apt' ? 'ufw 가 inactive 면 필요 없습니다.' : 'firewalld 가 not running 이면 필요 없습니다.',
  });

  if (variant === 'native') {
    steps.push({
      id: 'libs',
      title: 'MongoDB 라이브러리',
      when: '설치기가 "빠진 라이브러리" 와 함께 명령을 보여 줄 때만(호환 모드는 필요 없음)',
      commands: pm === 'apt' ? 'sudo apt-get install -y libcurl4' : 'sudo dnf install -y libcurl',
      note: '예시입니다. 설치기가 보여 주는 명령이 있으면 그것을 씁니다.',
    });
  }

  // 서버의 Docker 는 root 인 dockerd 가 포트를 열어 낮은 포트도 된다(설치기와 같은 규칙).
  if (port < 1024 && !(variant === 'docker' && input.dockerMode === 'system')) {
    steps.push({
      id: 'low-port',
      title: '1024 미만 포트',
      when: `${port} 번처럼 1024 아래 포트를 쓸 때 — 4600 같은 포트를 쓰면 필요 없습니다`,
      commands: [
        `echo 'net.ipv4.ip_unprivileged_port_start=${port}' | sudo tee /etc/sysctl.d/60-signageone-port.conf`,
        'sudo sysctl --system',
      ].join('\n'),
    });
  }

  return steps;
}

/** 관리자에게 한 번에 보낼 글 — 단계마다 제목·조건을 주석으로 단다. */
export function adminScript(input: AdminInput, steps: readonly AdminStep[]): string {
  const header = [
    `# SignageOne 서버 설치 — 관리자(sudo) 사전 작업 (${VARIANT_TITLE[input.variant]})`,
    `# 설치할 사용자: ${input.user} · 설치 위치: ${input.prefix.replace(/\/+$/, '')} · 포트: ${input.port}`,
    '# 이 서버에 해당하는 단계만 실행하세요. 설치할 사용자가 "sh install.sh --check" 로 필요한 것을 확인할 수 있습니다.',
  ];
  const body = steps.map((step, at) =>
    [`# ${at + 1}. ${step.title} — ${step.when}`, ...(step.check ? [`#    확인: ${step.check}`] : []), step.commands].join('\n'),
  );
  return [header.join('\n'), ...body].join('\n\n');
}
